import type { LogLevel } from '../../../../shared/boothLog'
import { boothLog, describeError } from '../telemetry/boothLogClient'
import { indexByName, type ConfigNode } from './presetCompatibility'

/**
 * Camera behaviour, written to the booth log file as `[camera]` lines.
 *
 * A field report like "the Nikon D5100 often shows this screen" could not be
 * checked against anything: the camera lives in the renderer, whose console
 * never left the booth. These lines say, in order, what the camera did: which
 * body connected through which path and how long it took, what it reported
 * about itself, when the health check stopped getting frames and why, every
 * disconnect with its cause, each shutter press, and every camera error screen
 * the operator or guest saw.
 *
 * Nothing here talks to the camera. Every value comes from a call the app makes
 * anyway, so logging can never change what the camera does. Every function
 * swallows its own failures.
 */

type CameraKey = 'primary' | 'secondary'

export type ConnectPath = 'webcam' | 'native' | 'soft' | 'chooser'

export type DisconnectReason =
  | 'unspecified'
  | 'connect-failed'
  | 'not-responding-after-connect'
  | 'wake-failed'
  | 'capture-recovery'
  | 'monitor-not-responding'
  | 'usb-unplug'

const safe = (fn: () => void): void => {
  try {
    fn()
  } catch {
    // Observability is never worth breaking a camera for.
  }
}

const emit = (
  level: LogLevel,
  event: string,
  message: string,
  data?: Record<string, unknown>,
  throttle?: { key?: string; maxPerWindow?: number; windowMs?: number }
): void =>
  boothLog.log({
    level,
    category: 'camera',
    event,
    message,
    data,
    throttleKey: throttle?.key,
    maxPerWindow: throttle?.maxPerWindow,
    windowMs: throttle?.windowMs
  })

/** A capture session takes several photos within one throttle window. */
const CAPTURE_LINES_PER_WINDOW = 30

/**
 * An error screen can stay up for hours (a booth left on the camera prompt
 * overnight), and a Preview re-sets its error on every retry: a few lines per
 * five minutes per text is the story; the rest is a count.
 */
const UI_ERROR_WINDOW_MS = 5 * 60 * 1000
const UI_ERROR_LINES_PER_WINDOW = 3

/**
 * The monitor probes every 5 s. Of a run of failures, the first few lines say
 * what happened; after that one line every 60 failures (about 5 minutes) says it
 * is still happening, and health.recovered says how long it lasted.
 */
const HEALTH_FIRST_LINES = 3
const HEALTH_EVERY_NTH = 60

const hex4 = (value: number | undefined | null): string | null =>
  typeof value === 'number' ? value.toString(16).padStart(4, '0') : null

/** What the USB descriptor says about a body: free to read, no camera traffic. */
export function usbIdentity(device: USBDevice | null | undefined): Record<string, unknown> | null {
  if (!device) return null
  try {
    return {
      usb: `${hex4(device.vendorId)}:${hex4(device.productId)}`,
      manufacturer: device.manufacturerName ?? null,
      product: device.productName ?? null,
      serial: device.serialNumber ?? null,
      usbDeviceVersion: `${device.deviceVersionMajor}.${device.deviceVersionMinor}.${device.deviceVersionSubminor}`
    }
  } catch {
    return null
  }
}

/**
 * The widgets that explain a body's behaviour, first name found wins. Nikon
 * and Canon name some of them differently; the mode dial is the one that
 * matters most for "it works in one mode and not another".
 */
const IDENTITY_WIDGETS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['model', ['cameramodel', 'model']],
  ['manufacturer', ['manufacturer']],
  ['firmware', ['deviceversion']],
  ['serial', ['serialnumber', 'eosserialnumber']],
  ['lens', ['lensname']],
  ['battery', ['batterylevel']],
  ['shutterCount', ['shuttercounter']],
  ['mode', ['autoexposuremodedial', 'autoexposuremode', 'expprogram', 'exposureprogram']],
  ['focusMode', ['focusmode', 'focusmode2', 'afmode']],
  ['captureTarget', ['capturetarget']],
  ['imageFormat', ['imageformat']],
  ['iso', ['iso']],
  ['shutterSpeed', ['shutterspeed']],
  ['aperture', ['aperture', 'f-number', 'fnumber']],
  ['autoPowerOff', ['autopoweroff', 'autooffphoto', 'autooffmenu', 'autoofflcd', 'autooffinfo']]
]

/** A summary of a config tree the app already read, or null when it is not one. */
export function summarizeCameraConfig(config: unknown): Record<string, string> | null {
  if (!config || typeof config !== 'object') return null
  const index = indexByName(config as ConfigNode)
  if (index.size === 0) return null
  const summary: Record<string, string> = {}
  for (const [field, names] of IDENTITY_WIDGETS) {
    for (const name of names) {
      const node = index.get(name)
      if (node && node.value !== undefined && node.value !== null && String(node.value) !== '') {
        summary[field] = String(node.value).slice(0, 80)
        break
      }
    }
  }
  return summary
}

const healthState: Record<
  CameraKey,
  { since: number; failures: number; reasons: Set<string> } | null
> = {
  primary: null,
  secondary: null
}

export const cameraLog = {
  connectStart(cameraKey: CameraKey, data: Record<string, unknown>): void {
    safe(() => emit('info', 'connect.start', `${cameraKey} connecting`, { cameraKey, ...data }))
  },

  /** The outcome of one tryToConnect, as camera_connect reports it, plus what that event leaves out. */
  connectResult(
    outcome: 'ok' | 'recovered' | 'failed',
    cameraKey: CameraKey,
    data: Record<string, unknown>
  ): void {
    safe(() => {
      const level: LogLevel =
        outcome === 'failed' ? 'error' : outcome === 'recovered' ? 'warn' : 'info'
      const event = outcome === 'failed' ? 'connect.fail' : `connect.${outcome}`
      emit(
        level,
        event,
        `${cameraKey} ${outcome === 'failed' ? 'failed to connect' : 'connected'}`,
        {
          cameraKey,
          ...data
        }
      )
    })
  },

  /** The Reconnect v2 soft path bound nothing usable; the chooser path is tried next. */
  softReconnectFailed(cameraKey: CameraKey, err: unknown, data?: Record<string, unknown>): void {
    safe(() =>
      emit('warn', 'connect.soft_fail', `${cameraKey} soft reconnect failed, trying the chooser`, {
        cameraKey,
        error: describeError(err),
        ...data
      })
    )
  },

  /** The first connect failed; the body is USB-reset and connected once more. */
  resetRetry(cameraKey: CameraKey, err: unknown): void {
    safe(() =>
      emit(
        'warn',
        'connect.reset_retry',
        `${cameraKey} connect failed, USB-resetting and retrying once`,
        {
          cameraKey,
          error: describeError(err)
        }
      )
    )
  },

  /** A connect the operator was waiting for gave up: timeout, dead after connect, or an error. */
  viewConnectFailed(cameraKey: CameraKey, err: unknown, data?: Record<string, unknown>): void {
    safe(() =>
      emit('error', 'connect.view_failed', `${cameraKey} connect from the booth screen failed`, {
        cameraKey,
        error: describeError(err),
        ...data
      })
    )
  },

  /** Who the body is, from the USB descriptor and the backend that drives it. */
  identify(cameraKey: CameraKey, data: Record<string, unknown>): void {
    safe(() =>
      emit(
        'info',
        'identify',
        `${cameraKey} body`,
        { cameraKey, ...data },
        {
          key: `camera|identify|${cameraKey}`
        }
      )
    )
  },

  /** What the body reports about itself, from a config tree the app read anyway. */
  identifyFromConfig(cameraKey: CameraKey, config: unknown): void {
    safe(() => {
      const summary = summarizeCameraConfig(config)
      if (!summary) return
      emit(
        'info',
        'identify.config',
        `${cameraKey} settings`,
        { cameraKey, ...summary },
        {
          key: `camera|identify.config|${cameraKey}`
        }
      )
    })
  },

  /**
   * The health check got no frame. Every failure is counted; a line is written for
   * the first few of a run, for each new reason, and then every HEALTH_EVERY_NTH.
   */
  healthFail(
    cameraKey: CameraKey,
    reason: 'no-frame' | 'error' | 'no-device',
    err?: unknown,
    data?: Record<string, unknown>
  ): void {
    safe(() => {
      const state = healthState[cameraKey] ?? {
        since: Date.now(),
        failures: 0,
        reasons: new Set<string>()
      }
      state.failures++
      healthState[cameraKey] = state
      const newReason = !state.reasons.has(reason)
      state.reasons.add(reason)
      if (
        !newReason &&
        state.failures > HEALTH_FIRST_LINES &&
        state.failures % HEALTH_EVERY_NTH !== 0
      ) {
        return
      }
      emit(
        'warn',
        'health.fail',
        `${cameraKey} not responding (${reason})`,
        {
          cameraKey,
          reason,
          failures: state.failures,
          ...(err === undefined ? {} : { error: describeError(err) }),
          ...data
        },
        { key: `camera|health.fail|${cameraKey}|${reason}` }
      )
    })
  },

  /** A frame arrived. Writes a line only when it ends a run of failures. */
  healthOk(cameraKey: CameraKey): void {
    safe(() => {
      const state = healthState[cameraKey]
      if (!state) return
      healthState[cameraKey] = null
      emit('info', 'health.recovered', `${cameraKey} responding again`, {
        cameraKey,
        failures: state.failures,
        downMs: Date.now() - state.since
      })
    })
  },

  disconnect(cameraKey: CameraKey, reason: DisconnectReason, data?: Record<string, unknown>): void {
    safe(() => {
      // A camera that is gone is not "recovering"; the next connect starts a fresh story.
      healthState[cameraKey] = null
      emit(
        reason === 'unspecified' ? 'info' : 'warn',
        'disconnect',
        `${cameraKey} disconnected (${reason})`,
        {
          cameraKey,
          reason,
          ...data
        }
      )
    })
  },

  captureOk(cameraKey: CameraKey, data: Record<string, unknown>): void {
    safe(() =>
      emit(
        'info',
        'capture.ok',
        `${cameraKey} photo captured`,
        { cameraKey, ...data },
        {
          key: `camera|capture.ok|${cameraKey}`,
          maxPerWindow: CAPTURE_LINES_PER_WINDOW
        }
      )
    )
  },

  captureRetry(cameraKey: CameraKey, err: unknown, data: Record<string, unknown>): void {
    safe(() =>
      emit(
        'warn',
        'capture.retry',
        `${cameraKey} shutter press failed, retrying`,
        { cameraKey, error: describeError(err), ...data },
        { key: `camera|capture.retry|${cameraKey}`, maxPerWindow: CAPTURE_LINES_PER_WINDOW }
      )
    )
  },

  captureFail(cameraKey: CameraKey, err: unknown, data: Record<string, unknown>): void {
    safe(() =>
      emit(
        'error',
        'capture.fail',
        `${cameraKey} capture failed`,
        { cameraKey, ...(err === undefined ? {} : { error: describeError(err) }), ...data },
        { key: `camera|capture.fail|${cameraKey}`, maxPerWindow: CAPTURE_LINES_PER_WINDOW }
      )
    )
  },

  /** A camera error the operator or guest saw on screen, in the words it used. */
  uiErrorShown(screen: string, text: string, data?: Record<string, unknown>): void {
    safe(() =>
      emit(
        'warn',
        'ui.error_shown',
        `${screen}: ${text}`.slice(0, 300),
        { screen, ...data },
        {
          key: `camera|ui.error_shown|${screen}|${text}`,
          maxPerWindow: UI_ERROR_LINES_PER_WINDOW,
          windowMs: UI_ERROR_WINDOW_MS
        }
      )
    )
  }
}

/** Test seam: the health streaks are module state. */
export function resetCameraLogStateForTests(): void {
  healthState.primary = null
  healthState.secondary = null
}
